import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import {
	activateUser,
	deactivateUser,
	removeUser,
	userUpdate,
} from '../../api';
import {
	createToken,
	deleteToken,
	getRoles,
	getTeam,
	getTokens,
	resendToken,
} from '../../api/company';

import {
	TemplateDropdown,
	TemplateField,
} from '../../components/Attributes/Attributes_old';
import {
	capitalizeFirstLetter,
	classNames,
	getUserFullName,
} from '../../components/helpers/functions';
import Modal from '../../components/Shell/Modal';
import { MainContent, PageHeading } from '../../components/Shell/Content';
import { AvatarGenerated } from '../../components/Avatar';
import Button from '../../components/Button';

import useAsync from '../../hooks/useAsync';
import useLocal from '../../hooks/useLocal';
import useModal from '../../hooks/useModal';
import DataTable from '../../components/DataTable';
import {
	deletePowerUser,
	getAllPowerUsers,
	toggleNotifications,
} from '../../api/power-users';
import usePrevious from '../../hooks/usePrevious';

const DEFAULT_FORM = {
	role_id: 'da13e70e-6713-4ec6-bfa6-018c5e2c4c04',
};

const Team = () => {
	const navigate = useNavigate();

	const { tab } = useParams();

	const { open: addPowerUserOpen, setOpen: onOpenAddPowerUser } =
		useModal('addPowerUser');

	const [form, setForm] = useState(DEFAULT_FORM);
	const [isChangeRoleModalOpen, setChangeRoleModalOpen] = useState(false);

	const { get } = useLocal();
	const localUser = get('user');

	const [isCreateModalOpen, setCreateModalOpen] = useState(false);

	console.log('tab: ', tab);

	useEffect(() => {
		if (tab === 'new') {
			setCreateModalOpen(true);
		}
	}, [tab]);

	const { value: team, execute: refreshTeam } = useAsync(getTeam);
	const { value: powerUsers, execute: refreshPowerUsers } =
		useAsync(getAllPowerUsers);
	const { value: tokens, execute: refreshTokens } = useAsync(getTokens);
	const { value: roles, execute: refreshRoles } = useAsync(getRoles);
	const { status: creatingToken, execute: onCreateToken } = useAsync(
		createToken,
		{
			immediate: false,
			showNotification: {
				success: 'A user has been create.  Email sent.',
				error: 'raw',
			},
		},
	);
	const { status: activatingUser, execute: onActivateUser } = useAsync(
		activateUser,
		{
			immediate: false,
			showNotification: { success: 'User activated', error: 'raw' },
		},
	);
	const { status: deactivatingUser, execute: onDeactivateUser } = useAsync(
		deactivateUser,
		{
			immediate: false,
			showNotification: { success: 'User deactivated', error: 'raw' },
		},
	);

	const { execute: onDeletePowerUser } = useAsync(deletePowerUser, {
		immediate: false,
	});

	const { execute: onUpdatePowerUser } = useAsync(toggleNotifications, {
		immediate: false,
		showNotification: {
			success: 'Notifications updated',
			error: 'Uh oh...something went wrong',
		},
	});

	const { execute: onResendToken } = useAsync(resendToken, {
		immediate: false,
		showNotification: { success: 'Email sent', error: 'raw' },
	});

	const { execute: onDeleteToken } = useAsync(deleteToken, {
		immediate: false,
		showNotification: { success: 'Invitation deleted', error: 'raw' },
	});

	const { execute: onRemoveUser } = useAsync(removeUser, {
		immediate: false,
		showNotification: { success: 'User removed' },
	});

	const { execute: onUpdateUser, status: updatingRole } = useAsync(userUpdate, {
		immediate: false,
		showNotification: { success: 'User updated' },
	});

	const prevAddPowerUserOpen = usePrevious(addPowerUserOpen);
	useEffect(() => {
		if (!addPowerUserOpen && prevAddPowerUserOpen) {
			refreshPowerUsers();
		}
	}, [addPowerUserOpen]);

	useEffect(() => {
		if (!isCreateModalOpen || !isChangeRoleModalOpen) {
			setForm(DEFAULT_FORM);
			refreshTeam(null, null, { silent: true });
			refreshTokens(null, null, { silent: true });
		}
	}, [isCreateModalOpen, isChangeRoleModalOpen]);

	const activeTeam = team?.rows?.filter((u) => u.active);
	const inactiveTeam = team?.rows?.filter((u) => !u.active);

	const tabs = [
		{
			name: 'Active Accounts',
			onClick: () => navigate('/manage/team/active'),
			count: activeTeam?.length,
			current: tab === 'active' || !tab,
		},
		{
			name: 'Pending',
			onClick: () => navigate('/manage/team/pending'),
			count: tokens?.rows?.length,
			current: tab === 'pending',
		},
		{
			name: 'Inactive',
			onClick: () => navigate('/manage/team/inactive'),
			count: inactiveTeam?.length,
			current: tab === 'inactive',
		},
	];

	let source = [];

	if (!tab || (tab === 'active' && team)) {
		source = activeTeam;
	}

	if (tab === 'pending' && tokens) {
		source = tokens?.rows;
	}

	if (tab === 'inactive' && team) {
		source = inactiveTeam;
	}

	if (tab === 'power-users' && powerUsers) {
		source = powerUsers;
	}

	let actions = null;

	if (!tab || tab === 'active') {
		actions = [
			{
				name: 'Change Role',
				hidden: (user) => user.id === localUser.id,
				onClick: (info) =>
					setChangeRoleModalOpen({
						id: info?.original?.id,
						role_id: info?.original?.role_id,
					}),
			},
			{
				name: 'Deactivate',
				hidden: (user) => user.id === localUser.id,
				danger: 'Are you sure you want to deactivate this user?',
				onClick: (info) =>
					onDeactivateUser({ id: info?.original?.id }, () =>
						refreshTeam(null, null, { silent: true }),
					),
			},
		];
	} else if (tab === 'pending') {
		actions = [
			{
				name: 'Resend Invite',
				onClick: (info) => onResendToken({ id: info?.original?.id }),
			},
			{
				name: 'Delete Invite',
				danger: 'Are you sure you want to delete this invite?',
				onClick: (info) =>
					onDeleteToken({ id: info?.original?.id }, () =>
						refreshTokens(null, null, { silent: true }),
					),
			},
		];
	} else if (tab === 'inactive') {
		actions = [
			{
				name: 'Reactivate',
				onClick: (info) =>
					onActivateUser({ id: info?.original?.id }, () =>
						refreshTeam(null, null, { silent: true }),
					),
			},
			{
				name: 'Delete User',
				danger: 'Are you sure you want to delete this user?',
				onClick: (info) =>
					onRemoveUser({ id: info?.original?.id }, () =>
						refreshTeam(null, null, { silent: true }),
					),
			},
		];
	} else if (tab === 'power-users') {
		actions = [
			{
				name: 'Delete Power User',
				danger: 'Are you sure you want to delete this power user?',
				onClick: (info) => {
					onDeletePowerUser(info?.original?.id, () =>
						refreshPowerUsers(null, null, { silent: true }),
					);
				},
			},
		];
	}

	const { updatedRoleId, ...restForm } = form;

	return (
		<>
			<MainContent maxWidth="max-w-7xl">
				<PageHeading
					actions={[
						{
							name: 'Add Power User',
							type: 'secondary',
							onClick: () => onOpenAddPowerUser(),
						},
						{
							name: 'Add User Account',
							onClick: () => navigate('/manage/team/new'),
						},
					]}
					divider={false}
				>
					Manage Team
				</PageHeading>

				<div className="mb-4">
					<div className="grid grid-cols-12">
						<div className="col-span-3">
							<nav
								className={classNames('flex flex-1 flex-col mt-4')}
								aria-label="Sidebar"
							>
								<ul role="list" className="-mx-2 space-y-1">
									{[
										{
											name: 'Active',
											onClick: () => {
												navigate('/manage/team');
											},
											current: !tab,
											count: activeTeam?.length,
										},
										{
											name: 'Pending',
											onClick: () => {
												navigate('/manage/team/pending');
											},
											current: tab === 'pending',
											count: tokens?.rows?.length,
										},
										{
											name: 'Inactive',
											onClick: () => {
												navigate('/manage/team/inactive');
											},
											current: tab === 'inactive',
											count: inactiveTeam?.length,
											divider: true,
										},
										{
											name: 'Power Users',
											onClick: () => {
												navigate('/manage/team/power-users');
											},
											current: tab === 'power-users',
											count: powerUsers?.length,
										},
									].map((item) => {
										return (
											<>
												<li key={item.name}>
													<div
														onClick={() => item.onClick()}
														className={classNames(
															item.current
																? 'bg-gray-50 text-indigo-600'
																: 'text-gray-700 hover:text-indigo-600 hover:bg-gray-50',
															'group flex gap-x-3 rounded-md p-2 text-xs leading-6 font-medium cursor-pointer',
														)}
													>
														{item.icon && (
															<item.icon
																className={classNames(
																	item.current
																		? 'text-indigo-600'
																		: 'text-gray-400 group-hover:text-indigo-600',
																	'h-6 w-6 shrink-0',
																)}
																aria-hidden="true"
															/>
														)}
														{item.name}
														{typeof item.count !== 'undefined' ? (
															<span
																className="ml-auto w-9 min-w-max whitespace-nowrap rounded-full bg-white px-2.5 py-0.5 text-center text-xxs font-medium leading-5 text-gray-600 ring-1 ring-inset ring-gray-200"
																aria-hidden="true"
															>
																{item.count || 0}
															</span>
														) : null}
													</div>
												</li>
												{item?.divider ? (
													<div className="my-5 border-b border-gray-100"></div>
												) : null}
											</>
										);
									})}
								</ul>
							</nav>
						</div>
						<div className="col-span-9 mx-2 px-4 mt-4">
							<DataTable
								title={
									!tab
										? 'Active Accounts'
										: tab === 'power-users'
											? 'Power users'
											: `${capitalizeFirstLetter(tab)}`
								}
								columns={[
									{
										header: '',
										accessorKey: 'avatar',
										enableSorting: false,
										cell: (info) => {
											const row = info?.row?.original;
											return (
												<AvatarGenerated
													size="xs"
													id={getUserFullName({
														firstName: row?.firstName,
														lastName: row?.lastName,
														...row,
													})}
												/>
											);
										},
									},
									{
										header: 'Email',
										accessorKey: 'email',
										cell: (info) => info?.getValue() || '',
									},
									...(tab !== 'pending'
										? [
												{
													header: 'Name',
													accessorKey: 'name',
													enableSorting: false,

													cell: (info) => {
														const row = info?.row?.original;
														return row.firstName ||
															row.lastLame ||
															row.first_name ||
															row.last_name
															? getUserFullName(row)
															: 'Not provided';
													},
												},
											]
										: []),

									...(tab !== 'power-users'
										? [
												{
													header: 'Role',
													accessorKey: 'role.displayName',
													cell: (info) => info?.getValue() || '',
												},
											]
										: []),

									...(tab === 'pending'
										? [
												{
													header: 'Token',
													accessorKey: 'token',
													cell: (info) => info?.getValue() || '',
												},
											]
										: []),
									...(tab === 'pending'
										? [
												{
													header: 'Expires',
													accessorKey: 'expires',
													cell: (info) => info?.getValue() || '',
												},
											]
										: []),

									/*{
										header: 'Notifications',
										accessorKey: 'notifications',
										enableSorting: false,
										cell: (info) => {
											const row = info?.row?.original;

											return (
												<TemplateBoolean
													value={row?.notifications}
													onChange={(index, user) => {
														onUpdatePowerUser(
															{
																id: row?.id,
																notifications: !row?.notifications,
															},
															() => {
																refreshPowerUsers(null, null, { silent: true });
															}
														);
													}}
												/>
											);
										},
									},*/
								]}
								data={{ rows: source }}
								rowActions={actions}
								hasCheckboxes={false}
								usePagination={false}
								useExport={false}
								manualSorting={false}
								useSearch={false}
								initialState={{
									sortBy: [
										{
											id: 'email',
											desc: false,
										},
									],
								}}
							/>
						</div>
					</div>
				</div>
			</MainContent>
			<Modal
				title="Send invite"
				open={isCreateModalOpen}
				onClose={() => {
					setCreateModalOpen(false);
					history.goBack(); //('/manage/team');
				}}
			>
				<div>
					<TemplateField
						name="Email"
						onChange={(index, email) => setForm({ ...form, email })}
						required
						className="mb-4"
					/>
					<TemplateDropdown
						name="Role"
						options={roles?.map((role) => ({
							label: role.display_name,
							value: role.id,
						}))}
						onChange={(index, role_id) => setForm({ ...form, role_id })}
						className="mb-4"
						value={form?.role_id}
						required
					/>
					<TemplateField
						name="First Name"
						onChange={(index, first_name) => setForm({ ...form, first_name })}
						optional
						className="mb-4"
					/>
					<TemplateField
						name="Last Name"
						onChange={(index, last_name) => setForm({ ...form, last_name })}
						optional
						className="mb-4"
					/>

					<div className="flex justify-end">
						<Button
							disabled={
								!['email', 'role_id'].every((a) =>
									Object.keys(restForm).includes(a),
								)
							}
							onClick={() =>
								onCreateToken(restForm, () => {
									setCreateModalOpen(false);
									navigate('/manage/team/pending');
								})
							}
						>
							{creatingToken === 'pending'
								? 'Sending invite...'
								: 'Send Invite'}
						</Button>
					</div>
				</div>
			</Modal>
			<Modal
				open={!!isChangeRoleModalOpen}
				onClose={() => setChangeRoleModalOpen(false)}
				title="Change user role"
			>
				<TemplateDropdown
					options={roles?.map((role) => ({
						label: role.display_name,
						value: role.id,
					}))}
					onChange={(index, updatedRoleId) =>
						setForm({ ...form, updatedRoleId })
					}
					className="mb-4"
					value={updatedRoleId || isChangeRoleModalOpen?.role_id}
				/>
				<div className="flex justify-end">
					<Button
						disabled={!['role_id'].every((a) => Object.keys(form).includes(a))}
						onClick={() =>
							onUpdateUser(
								{ id: isChangeRoleModalOpen?.id, role_id: form?.updatedRoleId },
								() => {
									setChangeRoleModalOpen(false);
									navigate('/manage/team');
								},
							)
						}
					>
						{updatingRole === 'pending' ? 'Updating...' : 'Update'}
					</Button>
				</div>
			</Modal>
		</>
	);
};

export default Team;
